import { Checkbox, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { renderTitle } from './getSalesAttributionColumns';
import { formatNumWithKMB, formatNumWithKMBAlt, formatString, getPriceTresholdValue } from './helperMethods';
import _ from 'lodash';
import { colorConstants } from 'config/colorConstants';
import UpAltSvg from 'assets/svg/UpAlt';
import DownSvg from 'assets/svg/Down';
import PositiveTriangle from 'assets/svg/PositiveTriangle';
import NegativeTriangle from 'assets/svg/NegativeTriangle';
import { KpiMetricType } from 'contexts/PriceSimulatorContext';
import DeleteOutlined from 'assets/svg/DeleteOutlined';
import { CircleFilled, Copy, Eye, EyeOff, Neutral } from 'assets/svg';
import EditPencilOutlined from 'assets/svg/EditPencilOutlined';
import { trackEventMixpanel } from './mixpanel';
import { PSR_EDIT_EVENT_CLICKED } from './mixpanelEventNames';

export const getAllScenarioListColumns = (
    data: any,
    onCopyClick: Function,
    onDeleteClick: Function,
    ignoreColumns: Array<string>,
    dataSources: Array<any>,
    loggedInUser: string,
) => {
    const seenUsers: any = new Set();
    const uniqueUsers: Array<any> = [];

    data?.forEach((item: any) => {
        if (!seenUsers.has(item['User Name'])) {
            seenUsers.add(item['User Name']);
            uniqueUsers.push({ text: item['User Name'], value: item['User Name'] });
        }
    });

    const filterOptions: any = {
        'User Name': uniqueUsers,
        'Data Source': dataSources.map((item: any) => {
            return { text: item.label, value: item.label };
        }),
    };

    let result: any[] = [];
    let tempCols = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]) : [];
    tempCols.push('Actions');
    const columns = tempCols.filter((name: string) => !ignoreColumns.includes(name));

    let children: any[] = [];

    result = columns.map((columnName, idx) => {
        const hasFilter = ['User Name', 'Data Source'].includes(columnName);
        const requireFilter = columnName === 'User Name';
        const filterOpitons = filterOptions[columnName] || undefined;

        return {
            title: renderTitle(
                idx === columns?.length - 1 ? '' : columnName,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: columnName,
            width: requireFilter ? 150 : 120,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            filters: filterOpitons,
            filterSearch: hasFilter,
            onFilter: (value: string, record: any) => hasFilter && record[columnName]?.indexOf(value) === 0,
            render: (columnData: any, rowData: any, rowIndex: number) => {
                const scenarioId = rowData?.scenarioId || 0;
                const isCreatedByLoggedInUser = rowData['User Name'] === loggedInUser;

                // scenario name column
                if (idx === 0) {
                    return (
                        <Link
                            to={`/pricing/recommendation/price-simulator/view/${rowData.scenarioId} `}
                            className=" cursor-pointer text-purpleFont  hover:text-purpleFont  font-medium py-1  text-start"
                        >
                            {columnData}
                        </Link>
                    );
                }

                if (idx === columns?.length - 1) {
                    // actions columns
                    return (
                        <div
                            className={`flex justify-between items-center ${
                                isCreatedByLoggedInUser ? 'cursor-pointer' : ''
                            } `}
                        >
                            <span onClick={() => onCopyClick(scenarioId)}>
                                <Copy />
                            </span>

                            {isCreatedByLoggedInUser ? (
                                <span>
                                    <Link
                                        to={`/pricing/recommendation/price-simulator/edit/${scenarioId} `}
                                        className=" cursor-pointer text-purpleFont  hover:text-purpleFont  font-medium py-1  text-start"
                                        onClick={() =>
                                            trackEventMixpanel(PSR_EDIT_EVENT_CLICKED, {
                                                scenarioId: scenarioId,
                                            })
                                        }
                                    >
                                        <EditPencilOutlined stroke={colorConstants.fontGrey} />
                                    </Link>
                                </span>
                            ) : (
                                <span className="cursor-not-allowed">
                                    <EditPencilOutlined stroke={colorConstants.greyAlt3} />
                                </span>
                            )}
                            <span
                                onClick={() => {
                                    isCreatedByLoggedInUser && onDeleteClick(scenarioId);
                                }}
                                className={`${isCreatedByLoggedInUser ? '' : 'cursor-not-allowed'}`}
                            >
                                <DeleteOutlined
                                    stroke={isCreatedByLoggedInUser ? colorConstants.fontGrey : colorConstants.greyAlt3}
                                />
                            </span>
                        </div>
                    );
                }
                return <div>{columnData}</div>;
            },
        };
    });

    return result;
};

export const getInputPriceSimulatorColumns = (
    isComparisonMode: boolean,
    viewMode: boolean,
    levels: Array<string>,
    level1Index: null | number,
    setLevel1Index: Function,
    level2Index: null | number,
    setLevel2Index: Function,
    rowHeader: string,
    inputChangeHandler: (
        value: any,
        rowData: any,
        columnaName: any,
        isNewPriceChange: boolean,
        roundingDecimal: number,
    ) => void,
    globalPercentageChangeHandler: (value: string, columnName: string, roundingDecimal: number) => void,
    triggerCopyOrLinkFunctionality: Function,
    onCompFollowClick: Function,
    selectedInputKpiOptions: Array<any>,
) => {
    const tableMapping: any = {
        'COGS Rate': [
            { label: 'Current', value: 'cp' },
            { label: 'New', value: 'np' },
            { label: '% Chg', value: 'pc' },
        ],
        'List Price': [
            { label: 'Current', value: 'cp' },
            { label: 'New', value: 'np' },
            { label: '% Chg', value: 'pc' },
        ],
        'Everyday Retail Price': [
            { label: 'Current', value: 'cp' },
            { label: 'New', value: 'np' },
            { label: '% Chg', value: 'pc' },
        ],
        'Trade Rate': [
            { label: 'Current', value: 'cp' },
            { label: 'New', value: 'np' },
            { label: '% Chg', value: 'pc' },
        ],
        'ACV Distribution': [
            { label: 'Current ', value: 'cp' },
            { label: 'New', value: 'np' },
            { label: '% Chg', value: 'pc' },
        ],
        'OI Rate': [
            { label: 'Current ', value: 'cp', roundingDecimal: 3 },
            { label: 'New', value: 'np', roundingDecimal: 3 },
            { label: '% Chg', value: 'pc', roundingDecimal: 3 },
        ],
        'Non OI Rate': [
            { label: 'Current ', value: 'cp', roundingDecimal: 3 },
            { label: 'New', value: 'np', roundingDecimal: 3 },
            { label: '% Chg', value: 'pc', roundingDecimal: 3 },
        ],
        'Promo Trade Rate Spends': [
            { label: 'Current ', value: 'cp', roundingDecimal: 3 },
            { label: 'New', value: 'np', roundingDecimal: 3 },
            { label: '% Chg', value: 'pc', roundingDecimal: 3 },
        ],
        'Comp Follow': [{ label: 'Yes Or No', value: 'cp' }],
    };

    // for comparision mode we dont current price for the KPI's
    if (isComparisonMode) {
        Object.keys(tableMapping).forEach((keyString: string) => {
            if (keyString !== 'List Price' && keyString !== 'Comp Follow') {
                tableMapping[keyString].shift();
            }
        });
    }

    let result: any[] = [];
    let columns = [...selectedInputKpiOptions.map((item) => item.label)];

    if (isComparisonMode) {
        columns.unshift('label');
    }

    // empty column for copy functionality
    if (!isComparisonMode) {
        columns.push('');
    }

    let children: any[] = [];
    result = columns.map((columnName, idx) => {
        if (idx >= 0) {
            children = tableMapping?.[columnName]?.map((item: any, index: number) => {
                return {
                    title: () => {
                        if (columnName === 'Comp Follow' && !isComparisonMode) {
                            return (
                                <div className="flex flex-col justify-center items-center">
                                    {renderTitle(
                                        item?.label,
                                        'center',
                                        undefined,
                                        'text-purpleFont !font-bold',
                                        graphXAxisLabelFontSize,
                                    )}
                                    <div className="flex items-center justify-center w-full">
                                        <Checkbox
                                            disabled={viewMode}
                                            onChange={(e) => onCompFollowClick(e.target.checked, -1, columnName, true)}
                                        ></Checkbox>
                                    </div>
                                </div>
                            );
                        }
                        if (index == 2 && !isComparisonMode) {
                            return (
                                <div className="flex flex-col justify-center items-center">
                                    <div className="flex items-center justify-center w-full">
                                        <input
                                            onChange={(e) =>
                                                globalPercentageChangeHandler(
                                                    e.target.value,
                                                    columnName,
                                                    item?.roundingDecimal ?? 2,
                                                )
                                            }
                                            className=" w-16 rounded  border border-purpleFont outline-none px-3 py-1 mr-2"
                                            placeholder="0.00"
                                            disabled={viewMode}
                                        />
                                        {'%'}
                                    </div>
                                </div>
                            );
                        } else {
                            return renderTitle(
                                item?.label,
                                'center',
                                undefined,
                                'text-purpleFont !font-bold',
                                graphXAxisLabelFontSize,
                            );
                        }
                    },
                    dataIndex: columnName,
                    width: 120,
                    key: columnName,
                    onCell: (rowData: any, rowIndex: any) => {
                        const hideNestedData =
                            rowData['nestedIndex'] !== null &&
                            ![level1Index, level2Index].includes(rowData['nestedIndex']);
                        return {
                            style: {
                                textAlign: 'center',
                                backgroundColor:
                                    level1Index !== null &&
                                    (rowData['nestedIndex'] === level1Index || rowIndex === level1Index)
                                        ? colorConstants.lightPurple
                                        : 'white',
                                borderRight:
                                    index < tableMapping?.[columnName]?.length - 1 || isComparisonMode
                                        ? 'none'
                                        : '1px solid rgba(0,0,0,.06)',
                            },
                            colSpan: hideNestedData ? 0 : 1,
                        };
                    },
                    render: (columnData: any, rowData: any, rowIndex: number) => {
                        const cellObj = columnData && 'nested' in columnData && columnData['nested']?.[item?.value];

                        if (columnName === 'Comp Follow') {
                            if (isComparisonMode || viewMode) {
                                return cellObj.val ? 'Yes' : 'No';
                            }

                            return (
                                <Checkbox
                                    checked={cellObj.val}
                                    onChange={(e) => onCompFollowClick(e.target.checked, rowIndex, columnName, false)}
                                    style={{ margin: '7px' }}
                                ></Checkbox>
                            );
                        }

                        const isRetailPriceAndCrossedTreshold =
                            columnName === 'Everyday Retail Price' &&
                            cellObj?.val > getPriceTresholdValue(rowData[columnName]['nested']?.cp?.val || 0);
                        const roundingDecimal = item?.roundingDecimal ?? 2;
                        return (
                            <div className="flex justify-center font-xs items-center">
                                {cellObj?.pre}
                                {/* current value  */}
                                {index === 0 &&
                                    (_.isNil(cellObj?.val)
                                        ? ''
                                        : formatNumWithKMBAlt(cellObj?.val, roundingDecimal, roundingDecimal))}
                                {/* new price  */}
                                {index === 1 &&
                                    (isComparisonMode ? (
                                        formatNumWithKMBAlt(cellObj?.rval, roundingDecimal, roundingDecimal)
                                    ) : (
                                        <input
                                            onChange={(e) =>
                                                inputChangeHandler(
                                                    e.target.value,
                                                    rowData,
                                                    columnName,
                                                    true,
                                                    roundingDecimal,
                                                )
                                            }
                                            value={cellObj?.val}
                                            className={` w-16 rounded  border ${
                                                isRetailPriceAndCrossedTreshold
                                                    ? 'border-orangeFont'
                                                    : 'border-purpleFont '
                                            }  outline-none px-3 py-1 `}
                                            placeholder={formatNumWithKMBAlt(
                                                cellObj?.rval,
                                                roundingDecimal,
                                                roundingDecimal,
                                            )}
                                            disabled={viewMode}
                                        />
                                    ))}
                                {/* percentage change */}
                                {index === 2 &&
                                    (isComparisonMode ? (
                                        formatNumWithKMBAlt(cellObj?.rval, 2, 2) //always display 2 decimal places for percentage
                                    ) : (
                                        <>
                                            <input
                                                onChange={(e) =>
                                                    inputChangeHandler(
                                                        e.target.value,
                                                        rowData,
                                                        columnName,
                                                        false,
                                                        roundingDecimal,
                                                    )
                                                }
                                                value={cellObj?.val}
                                                placeholder={cellObj?.rval}
                                                className=" w-16 rounded  border border-purpleFont outline-none px-3 py-1 mr-2"
                                                disabled={viewMode}
                                            />
                                            {cellObj?.post}
                                        </>
                                    ))}
                            </div>
                        );
                    },
                };
            });
        } else children = [];
        return {
            title: renderTitle(
                idx === 0 && isComparisonMode ? rowHeader : columnName,
                idx === 0 && isComparisonMode ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: columnName,
            width: idx === columns?.length - 1 ? 20 : 190,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);
                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                        backgroundColor:
                            level1Index !== null && (rowData['nestedIndex'] === level1Index || rowIndex === level1Index)
                                ? colorConstants.lightPurple
                                : 'white',
                        borderRight: idx === columns?.length - 1 ? 'none' : 'auto',
                    },
                    colSpan: hideNestedData ? 0 : 1,
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);
                if (isComparisonMode && idx === 0) {
                    return (
                        <div className="font-xs text-greyFont font-medium">
                            {columnData}
                            {idx === 0 && (
                                <span
                                    onClick={() => {
                                        const level = rowData?.level;
                                        if (level === levels[0]) {
                                            if (level1Index === rowIndex) {
                                                setLevel1Index(null);
                                            } else setLevel1Index(rowIndex);
                                            setLevel2Index(null);
                                        } else if (levels.length > 1 && level == levels[1]) {
                                            if (level2Index === rowIndex) setLevel2Index(null);
                                            else setLevel2Index(rowIndex);
                                        }
                                    }}
                                    className="cursor-pointer flex justify-start items-center"
                                >
                                    {rowData?.level === levels[0] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">{levels[1]}</span>
                                            {level1Index === rowIndex ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}
                                    {levels.length > 2 && rowData?.level === levels[1] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">{levels[1]}</span>
                                            {level2Index === rowIndex ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}
                                </span>
                            )}
                        </div>
                    );
                }
                if (!hideNestedData && typeof columnData === 'string') {
                    return (
                        <div
                            style={{ marginLeft: rowData['isExpandable'] === false ? 10 : 0 }}
                            className="font-xs text-greyFont font-medium"
                        >
                            {columnData}
                        </div>
                    );
                }
                if (
                    !isComparisonMode &&
                    !hideNestedData &&
                    idx === columns.length - 1 &&
                    rowData['isExpandable'] &&
                    !viewMode
                ) {
                    return (
                        <span
                            onClick={() => {
                                triggerCopyOrLinkFunctionality(rowData);
                            }}
                            className="cursor-pointer flex justify-center items-center"
                        >
                            <Copy />
                        </span>
                    );
                }
            },
        };
    });
    return result;
};

export const getOutputPriceSimulatorColumns = (
    data: any,
    levels: Array<string>,
    level1Index: null | number,
    setLevel1Index: Function,
    level2Index: null | number,
    setLevel2Index: Function,
    rowHeader: string,
    outputKeyMapping: any,
) => {
    let result: any[] = [];
    const keys = Object.keys(outputKeyMapping);
    const columns = [
        'label',
        ...keys
            .map((k: string) => outputKeyMapping[k].label)
            // TODO - need fix from BE, will enable after this
            .filter((k: string) => !['New Profit Pool', 'Current Profit Pool'].includes(k)),
    ];

    let children: any[] = [];

    result = columns.map((columnName, idx) => {
        return {
            title: renderTitle(
                idx === 0 ? rowHeader : columnName,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: columnName,
            width: 120,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);

                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                        backgroundColor:
                            level1Index !== null && (rowData['nestedIndex'] === level1Index || rowIndex === level1Index)
                                ? colorConstants.lightPurple
                                : 'white',
                    },
                    colSpan: hideNestedData ? 0 : 1,
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);

                if (idx === 0) {
                    return (
                        <div className="font-xs text-greyFont font-medium">
                            {columnData}
                            {idx === 0 && (
                                <span
                                    onClick={() => {
                                        const level = rowData?.level;
                                        if (level === levels[0]) {
                                            if (level1Index === rowIndex) {
                                                setLevel1Index(null);
                                            } else setLevel1Index(rowIndex);
                                            setLevel2Index(null);
                                        } else if (levels.length > 1 && level == levels[1]) {
                                            if (level2Index === rowIndex) setLevel2Index(null);
                                            else setLevel2Index(rowIndex);
                                        }
                                    }}
                                    className="cursor-pointer flex justify-start items-center"
                                >
                                    {rowData?.level === levels[0] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">{levels[1]}</span>
                                            {level1Index === rowIndex ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}

                                    {levels.length > 2 && rowData?.level === levels[1] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">
                                                {levels.length > 2 && levels[1]}
                                            </span>
                                            {level2Index === rowIndex ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}
                                </span>
                            )}
                        </div>
                    );
                }
                if (!hideNestedData && typeof columnData === 'string') {
                    return (
                        <div
                            style={{ marginLeft: rowData['isExpandable'] === false ? 10 : 0 }}
                            className="font-xs text-greyFont font-medium"
                        >
                            {columnData}
                        </div>
                    );
                }
                if (!hideNestedData && typeof columnData === 'object') {
                    const cellObj = columnData;
                    if (_.isNil(cellObj?.val)) return null;
                    return (
                        <div className="flex justify-center font-xs items-center gap-2">
                            {renderIcon(cellObj?.change)}
                            {cellObj?.pre}
                            {isNaN(cellObj?.val) ? cellObj?.val : formatNumWithKMBAlt(cellObj?.val, 2, 2)}
                            {cellObj?.post}
                        </div>
                    );
                }
            },
        };
    });

    return result;
};

const renderRecommdationsOutputTitle = (
    title: string,
    position: string | null = 'center',
    className: string = '',
    fontSize: number = 12,
    characterLimit: number,
    isClickable: boolean,
    expandedColList: any,
    expandClick: Function,
) => {
    return (
        <>
            <div
                title={characterLimit ? title : undefined}
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: fontSize,
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                    padding: `10px 10px 10px ${position ? '10px' : '15px'}`,
                }}
                className={` justify-center  h-9 flex  ${className}`}
            >
                {characterLimit ? formatString(title, characterLimit) : title}{' '}
                {isClickable && (
                    <span onClick={() => expandClick(title)} className="ml-2 cursor-pointer">
                        {expandedColList[title] ? <Eye /> : <EyeOff />}
                    </span>
                )}
            </div>
        </>
    );
};

export const getRecommdationPriceSimulatorColumns = (
    levels: Array<string>,
    level1Index: null | number,
    setLevel1Index: Function,
    level2Index: null | number,
    setLevel2Index: Function,
    rowHeader: string,
    toggledColumns: any,
    toggleClickHandler: Function,
) => {
    // TODO: move all these to config
    const parentCols = [
        'label',
        'New list price recom',
        'Role',
        'Strategy',
        'Mix',
        'Growth',
        'Gap',
        'COGS & Realization',
    ];

    const childColsRelations: any = {
        Strategy: ['BPE', 'PPE'],
        Mix: ['Margin', 'BPE', 'PPE'],
        Growth: ['Gowth vs Category', 'PL vs Category'],
        Gap: ['Threshold', 'Comp. Index', 'Curve Index'],
        'COGS & Realization': ['COGS Forecast', 'Net to Retail', 'Gross to Net'],
    };

    let childCols = [
        'BPE',
        'PPE',
        'Margin',
        'BPE',
        'PPE',
        'Gowth vs Category',
        'PL vs Category',
        'Threshold',
        'Comp. Index',
        'Curve Index',
        'COGS Forecast',
        'Net to Retail',
        'Gross to Net',
    ];

    let result: any[] = [];

    let updatedCols: Array<string> = [];

    parentCols.forEach((item) => {
        updatedCols.push(item);
        if (Object.keys(toggledColumns).includes(item) && toggledColumns[item]) {
            updatedCols = [...updatedCols, ...childColsRelations[item]];
        }
    });

    result = updatedCols.map((columnName, idx) => {
        const titleClassNames = `pt-2 text-purpleFont !font-bold ${
            childCols.includes(columnName) ? 'items-end border-b' : 'items-center'
        }`;
        return {
            title: renderRecommdationsOutputTitle(
                idx === 0 ? rowHeader : columnName,
                idx === 0 ? null : 'center',
                titleClassNames,
                graphXAxisLabelFontSize,
                0,
                Object.keys(childColsRelations).includes(columnName),
                toggledColumns,
                toggleClickHandler,
            ),
            dataIndex: columnName,
            width: 140,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);

                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                        backgroundColor:
                            level1Index !== null && (rowData['nestedIndex'] === level1Index || rowIndex === level1Index)
                                ? colorConstants.lightPurple
                                : childCols.includes(columnName)
                                ? '#FAFAFC'
                                : 'white',
                    },
                    colSpan: hideNestedData ? 0 : 1,
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                const hideNestedData =
                    rowData['nestedIndex'] !== null && ![level1Index, level2Index].includes(rowData['nestedIndex']);
                if (idx === 0) {
                    return (
                        <div
                            className={`font-xs text-greyFont font-medium label-cell ${
                                rowData?.level === levels[levels.length - 1] ? 'empty' : ''
                            }`}
                        >
                            <Tooltip
                                placement="top"
                                color="white"
                                destroyTooltipOnHide
                                overlayStyle={{ borderRadius: 4 }}
                                overlayInnerStyle={{
                                    fontSize: graphXAxisLabelFontSize,
                                    color: colorConstants.darkGrey,
                                }}
                                title={columnData}
                            >
                                <div>
                                    {columnData?.substring(0, 15)}
                                    {columnData.length > 15 ? '...' : ''}
                                </div>
                            </Tooltip>

                            {idx === 0 && (
                                <span
                                    onClick={() => {
                                        const level = rowData?.level;
                                        if (level === levels[0]) {
                                            if (level1Index === rowData.index) {
                                                setLevel1Index(null);
                                            } else setLevel1Index(rowData.index);
                                            setLevel2Index(null);
                                        } else if (levels.length > 1 && level == levels[1]) {
                                            if (level2Index === rowData.index) setLevel2Index(null);
                                            else setLevel2Index(rowData.index);
                                        }
                                    }}
                                    className="cursor-pointer flex justify-start items-center"
                                >
                                    {rowData?.level === levels[0] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">{levels[1]}</span>
                                            {level1Index === rowData.index ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}

                                    {levels.length > 2 && rowData?.level === levels[1] && (
                                        <div className="flex justify-start items-center">
                                            <span className="text-greyFont font-xxs font-normal mr-1">
                                                {levels.length > 2 && levels[2]}
                                            </span>
                                            {level2Index === rowData.index ? <UpAltSvg /> : <DownSvg />}
                                        </div>
                                    )}
                                </span>
                            )}
                        </div>
                    );
                }
                if (!hideNestedData && typeof columnData === 'string') {
                    return (
                        <div
                            style={{ marginLeft: rowData['isExpandable'] === false ? 10 : 0 }}
                            className="font-xs text-greyFont font-medium"
                        >
                            {columnData}
                        </div>
                    );
                }
                if (!hideNestedData && typeof columnData === 'object') {
                    const cellObj = columnData;
                    if (_.isNil(cellObj?.val)) return null;
                    return (
                        <div className="flex justify-center font-xs items-center gap-2">
                            {renderIcon(cellObj?.change)}
                            {cellObj?.pre}
                            {isNaN(cellObj?.val) ? cellObj?.val : formatNumWithKMB(cellObj?.val, 2)}
                            {cellObj?.post}
                        </div>
                    );
                }
            },
        };
    });

    return result;
};

export const getGoalsKpiMetricsColumns = (
    viewMode: boolean,
    handleInputValuesChanges: Function,
    onDeleteClick: Function,
    simulationTimePeriodSelection: any[],
    kpiMetricsConfig: any[],
) => {
    const isSimulationPeriodSelected = !simulationTimePeriodSelection?.includes(null);
    return [
        {
            title: renderTitle('KPI', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'name',
        },
        {
            title: renderTitle('Current Value', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'currentValue',
            render: (p: any, record: KpiMetricType) => {
                const kpiItem = kpiMetricsConfig?.find((item) => item?.key == record?.key);
                if (kpiItem?.decimalToRoundOff) {
                    return (
                        <div id={`actualValue-${record.key}`} className="">
                            {isSimulationPeriodSelected
                                ? (record?.pre || '') +
                                  _.round(Number(record.actualValue) || 0, kpiItem?.decimalToRoundOff)?.toFixed(
                                      kpiItem?.decimalToRoundOff,
                                  ) +
                                  (record.post || '')
                                : '-'}
                        </div>
                    );
                }
                return (
                    <div id={`actualValue-${record.key}`} className="">
                        {isSimulationPeriodSelected
                            ? (record?.pre || '') +
                              _.round(Number(record.actualValue) || 0)?.toLocaleString() +
                              (record.post || '')
                            : '-'}
                    </div>
                );
            },
        },
        {
            title: renderTitle('Goal Value', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'goalValue',
            render: (d: any, record: KpiMetricType) => (
                <div id={`goalValue-${record.key}`} className=" flex items-center">
                    <input
                        onChange={(e) => handleInputValuesChanges(e.target.value, record.key, true)}
                        className="w-28 rounded  border border-purpleFont outline-none px-3 py-1"
                        value={record.goalValue}
                        disabled={viewMode}
                        placeholder={
                            isSimulationPeriodSelected ? _.round(Number(record.actualValue) || 0)?.toLocaleString() : ''
                        }
                    />
                    <span className="font-xxs ml-2">
                        {Number(record.goalValue) > 0 ? Number(record.goalValue)?.toLocaleString() : ''}
                    </span>
                </div>
            ),
        },
        {
            title: renderTitle('% Change', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'percentChange',
            render: (_: any, record: KpiMetricType) => (
                <div id={`percentChange-${record.key}`} className=" ">
                    <input
                        onChange={(e) => handleInputValuesChanges(e.target.value, record.key, false)}
                        className="w-28 rounded  border border-purpleFont outline-none px-3 py-1"
                        value={record.percentChange}
                        disabled={viewMode}
                        placeholder={isSimulationPeriodSelected ? '0' : ''}
                    />
                </div>
            ),
        },
        {
            title: '  ',
            render: (_: any, record: any) => (
                <button
                    onClick={(e) => !viewMode && onDeleteClick(record.key)}
                    className={`text-center  ${viewMode ? 'cursor-not-allowed' : ''} `}
                >
                    <DeleteOutlined stroke={`${viewMode ? 'grey' : colorConstants.fontGrey}`} />
                </button>
            ),
            width: '5%',
        },
    ];
};

const renderIcon = (change: any) => {
    switch (change) {
        case 1:
            return <PositiveTriangle />;
        case -1:
            return <NegativeTriangle />;
        case 0:
            return <Neutral />;
        default:
            return '';
    }
};

export const getPortfolioUpdateColumns = (
    data: any,
    ignoreColumns: Array<string>,
    rowHeader: string,
    onCellClickhandler: Function,
    onAddClickhandler: Function,
    expandedRowDetails: { columnName: string; rowName: string; rowIndex: number },
    viewMode: boolean,
) => {
    let result: any[] = [];
    let tempCols = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]) : [];

    const columns = tempCols.filter((name: string) => !ignoreColumns.includes(name));
    let children: any[] = [];

    result = columns.map((columnName, idx) => {
        return {
            title: renderTitle(
                idx === 0 ? rowHeader : columnName,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: columnName,
            width: 120,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                const isAddedCell = rowData[columnName]?.inputList?.length > 0;

                const isClickedCell =
                    columnName === expandedRowDetails.columnName && rowData.key === expandedRowDetails.rowIndex;
                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                        backgroundColor: isAddedCell ? '#f8f5ff' : isClickedCell ? '#FAFAFC' : '',
                        border: isClickedCell ? '1px solid #D7D6D9' : '',
                        borderBottom: isClickedCell ? 'none' : '',
                    },
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                if (idx === 0) {
                    return <div className="font-xs text-greyFont font-medium">{columnData}</div>;
                } else if (columnData.value === null) {
                    return (
                        <div
                            className={`text-fontGreyAlt1   ${!viewMode ? ' cursor-pointer' : ''}  `}
                            onClick={() => {
                                !viewMode && onAddClickhandler(columnName, rowData, rowIndex);
                            }}
                        >
                            + Add
                        </div>
                    );
                } else {
                    return (
                        <Tooltip
                            placement="top"
                            color="white"
                            destroyTooltipOnHide
                            overlayStyle={{ borderRadius: 4 }}
                            overlayInnerStyle={{
                                fontSize: graphXAxisLabelFontSize,
                                color: colorConstants.darkGrey,
                            }}
                            title={renderUpdatePortfolioKpiTitle(rowData, columnName)}
                        >
                            <div
                                className={`flex justify-center font-xs items-center gap-2 ${
                                    !viewMode ? ' cursor-pointer' : ''
                                } `}
                                onClick={() => !viewMode && onCellClickhandler(columnName, rowData)}
                            >
                                {formatNumWithKMB(columnData.value, 2)}
                            </div>
                        </Tooltip>
                    );
                }
            },
        };
    });

    return result;
};

const renderUpdatePortfolioKpiTitle = (rowData: any, columnName: any) => {
    return (
        <div className="" style={{ width: '240px', paddingLeft: '2px' }}>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Volume sales </span>
                <span className="font-medium text-fontGray  ">
                    {formatNumWithKMB(rowData.kpiList[columnName]?.volume, 2)}
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Units</span>
                <span className="font-medium text-fontGray  ">
                    {formatNumWithKMB(rowData.kpiList[columnName]?.units, 2)} Units
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Value Sales</span>
                <span className="font-medium text-fontGray  ">
                    ${formatNumWithKMB(rowData.kpiList[columnName]?.value, 2)}
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Gross Profit</span>
                <span className="font-medium text-fontGray  ">
                    ${formatNumWithKMB(rowData.kpiList[columnName]?.gross_profit, 2)}
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">% Margin</span>
                <span className="font-medium text-fontGray  ">
                    {formatNumWithKMB(rowData.kpiList[columnName]?.margin_percent, 2)}%
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Retailer Profit</span>
                <span className="font-medium text-fontGray  ">
                    ${formatNumWithKMB(rowData.kpiList[columnName]?.retailer_profit, 2)}
                </span>
            </div>
            <div className="flex justify-between  mb-3 pr-2 font-xxs">
                <span className=" text-fontGray font-normal">Status</span>
                <span className="font-medium text-fontGray  ">
                    {rowData[columnName].isActive ? (
                        <span className="flex items-center">
                            <CircleFilled /> <span className="ml-2">Active</span>
                        </span>
                    ) : (
                        <span className="flex items-center">
                            <CircleFilled stroke={colorConstants.red} /> <span className="ml-2">Inactive</span>
                        </span>
                    )}
                </span>
            </div>
        </div>
    );
};

export const getPortfolioUpdateMoreInfoColumns = (columns: Array<string>) => {
    let result: any[] = [];

    result = columns.map((columnName, idx) => {
        return {
            title: renderTitle(
                columnName,
                idx === 0 ? null : 'center',
                undefined,
                'text-greyFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            dataIndex: columnName,
            width: 120,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                    },
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                if (idx === 0) {
                    return <div className="font-xs text-greyFont font-medium">{columnData}</div>;
                } else if (typeof columnData === 'object') {
                    const cellObj = columnData;

                    return (
                        <div className="flex justify-center font-xs items-center gap-2 cursor-pointer">
                            {renderIcon(cellObj?.change)}
                            {cellObj?.pre}
                            {isNaN(cellObj?.value) ? cellObj?.value : formatNumWithKMB(cellObj?.value, 2)}
                            {cellObj?.post}
                        </div>
                    );
                }
            },
        };
    });

    return result;
};

export const getNewKpiUpdateColumns = (
    viewMode: boolean,
    handleInputValuesChanges: Function,
    simulationTimePeriodSelection: any[],
) => {
    const isSimulationPeriodSelected = !simulationTimePeriodSelection?.includes(null);
    return [
        {
            title: renderTitle('KPI', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'label',
        },
        {
            title: renderTitle(
                'Recommended Value',
                '',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            dataIndex: 'rval',
            render: (p: any, record: any) => {
                return (
                    <div id={`rval-${record.key}`} className="">
                        {isSimulationPeriodSelected
                            ? (record?.pre || '') +
                              _.round(Number(record.rval) || 0, 2)?.toLocaleString() +
                              (record.post || '')
                            : '-'}
                    </div>
                );
            },
        },
        {
            title: renderTitle('New Value', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'value',
            render: (d: any, record: any) => {
                if (['value'].includes(record.key)) {
                    return (
                        <div className="w-28 rounded bg-greyBackgroundAlt cursor-not-allowed border border-purpleFont outline-none px-3 py-1">
                            {_.round(Number(record.value || record.rval) || 0, 2)?.toLocaleString()}
                        </div>
                    );
                }

                return (
                    <div id={`newValue-${record.key}`} className=" flex items-center">
                        <input
                            onChange={(e) => handleInputValuesChanges(e.target.value, record.key, true)}
                            className="w-28 rounded  border border-purpleFont outline-none px-3 py-1"
                            value={record.value}
                            disabled={viewMode}
                            placeholder={
                                isSimulationPeriodSelected ? _.round(Number(record.rval) || 0, 2)?.toLocaleString() : ''
                            }
                        />
                    </div>
                );
            },
        },
        {
            title: renderTitle('% Change', '', undefined, 'text-purpleFont !font-bold', graphXAxisLabelFontSize),
            dataIndex: 'percentChange',
            render: (d: any, record: any) => {
                if (['value'].includes(record.key)) {
                    return (
                        <div className="w-28 rounded bg-greyBackgroundAlt cursor-not-allowed border border-purpleFont outline-none px-3 py-1">
                            {record?.percentChange || 0}
                        </div>
                    );
                }

                return (
                    <div id={`percentChange-${record.key}`} className=" ">
                        <input
                            onChange={(e) => handleInputValuesChanges(e.target.value, record.key, false)}
                            className="w-28 rounded  border border-purpleFont outline-none px-3 py-1"
                            value={record.percentChange}
                            disabled={viewMode}
                            placeholder={'0'}
                        />
                    </div>
                );
            },
        },
    ];
};

export const getGoalsColumnsForSimulatorComparison = (data: any) => {
    let result: any[] = [];
    let columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]) : [];

    result = columns.map((columnName, idx) => {
        return {
            title: renderTitle(
                columnName,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            dataIndex: columnName,
            width: 120,
            key: columnName,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                return {
                    style: {},
                };
            },
            render: (columnData: any) => {
                if (idx === 0) {
                    return <div>{columnData}</div>;
                }
                if ([4, 5].includes(idx)) {
                    return (
                        <div className="flex justify-center items-center">
                            <span className="mr-2">
                                {/* render +/- sign */}
                                {columnData >= 0 ? <PositiveTriangle /> : <NegativeTriangle />}
                            </span>
                            <div>{formatNumWithKMB(columnData, 2)} </div>
                        </div>
                    );
                }
                return <div>{formatNumWithKMB(columnData, 2)} </div>;
            },
        };
    });

    return result;
};
